import {
  Button,
  Modal,
  Form,
  Input,
  Typography,
  DatePicker,
  Row,
  Col,
  InputNumber,
  Space,
  message,
  Spin,
} from "antd";
import React, {
  forwardRef,
  useState,
  useImperativeHandle,
} from "react";
import {
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
// import deleteIcon from "@/assets/images/icons/delete.png";

const ChangeAmountModal = forwardRef(
  ({ onSuccess }, ref) => {
    const [form] = Form.useForm();

    const [visible, setVisible] = useState(false);
    const [openLoading, setOpenLoading] =
      useState(false);
    const [submitLoading, setSubmitLoading] =
      useState(false);

    const [currentRecord, setCurrentRecord] =
      useState({});

    const [currentUserId, setCurrentUserId] =
      useState("");

    /**
     * 把后端日期转换成 dayjs
     */
    const parseDateValue = (value) => {
      if (!value) {
        return null;
      }

      if (dayjs.isDayjs(value)) {
        return value;
      }

      if (Array.isArray(value)) {
        const [year, month, day] = value;

        if (
          !year ||
          month == null ||
          !day
        ) {
          return null;
        }

        return dayjs(
          `${year}-${month}-${day}`,
          "YYYY-M-D"
        );
      }

      const result = dayjs(value);

      return result.isValid()
        ? result
        : null;
    };

    /**
     * 创建一条新的 Row
     */
    const getDefaultRow = (
      groupNo,
      userId = currentUserId
    ) => ({
      id: null,
      startDate: null,
      endDate: null,
      amount: null,
      remark: "",
      groupNo,
      userId,
      updateTime: dayjs(),
    });

    /**
     * 创建新的 Contract
     */
    const getDefaultContract = (
      groupNo,
      userId = currentUserId
    ) => ({
      groupNo,
      rows: [
        getDefaultRow(
          groupNo,
          userId
        ),
      ],
    });

    const groupByContract = (list = []) => {
      const groupMap = {};

      list.forEach((item) => {
        const groupNo =
          item?.groupNo || 1;

        if (!groupMap[groupNo]) {
          groupMap[groupNo] = {
            groupNo,
            rows: [],
          };
        }

        groupMap[groupNo].rows.push({
          ...item,

          id:
            item?.id ?? null,

          groupNo,

          userId: item?.userId || currentUserId,
          startDate:
            parseDateValue(
              item?.startDate
            ),

          endDate:
            parseDateValue(
              item?.endDate
            ),

          updateTime:
            parseDateValue(
              item?.updateTime
            ),
        });
      });

      /**
       * 按 groupNo 排序
       */
      return Object.values(
        groupMap
      ).sort(
        (a, b) =>
          Number(a.groupNo) -
          Number(b.groupNo)
      );
    };

    /**
     * 打开弹窗
     */
    useImperativeHandle(ref, () => ({
      open: async (record) => {
        setCurrentRecord(
          record || {}
        );

        setVisible(true);

        form.resetFields();

        setOpenLoading(true);

        try {
          const response =
            await http.get(
              api.getVendorMonthlyById,
              {
                params: {
                  id: record?.id,
                },
              }
            );

          const data =
            response?.data || {};

          setCurrentRecord(
            (prev) => ({
              ...prev,
              id:
                prev?.id ||
                data?.id,
            })
          );

          /**
           * UserId
           */
          const defaultUserId = localStorage.getItem("createBy");
          setCurrentUserId(defaultUserId);

          /**
           * 后端返回数据
           */
          const list =
            Array.isArray(
              data?.workerSalaryAdjustments
            ) &&
            data
              .workerSalaryAdjustments
              .length
              ? data.workerSalaryAdjustments
              : [
                  {
                    id: null,
                    groupNo: 1,
                    userId: defaultUserId,
                    updateTime: dayjs()
                  },
                ];

          /**
           * 根据 groupNo 分组
           */
          const contracts =
            groupByContract(list);

          /**
           * 设置 Form
           */
          form.setFieldsValue({
            contracts,
          });
        } catch (error) {
          form.setFieldsValue({
            contracts: [
              getDefaultContract(
                1,
                record?.usid || record?.userId ||""
              ),
            ],
          });

          message.error(
            "Failed to load data."
          );
        } finally {
          setOpenLoading(false);
        }
      },
    }));

    /**
     * 关闭
     */
    const handleCancel = () => {
      setVisible(false);

      form.resetFields();

      setCurrentRecord({});

      setCurrentUserId("");

      setOpenLoading(false);

      setSubmitLoading(false);
    };

    /**
     * 提交
     */
    const submitFn = async () => {
      try {
        const values =
          await form.validateFields();
        const workerSalaryAdjustments =
          (
            values?.contracts ||
            []
          )
            .flatMap(
              (contract) => {
                const groupNo =
                  contract?.groupNo;

                return (
                  contract?.rows ||
                  []
                ).map(
                  (item) => ({
                    /**
                     * 保留 id
                     */
                    id:
                      item?.id ??
                      null,

                    startDate:
                      item?.startDate?.format?.(
                        "YYYY-MM-DD"
                      ) || null,

                    endDate:
                      item?.endDate?.format?.(
                        "YYYY-MM-DD"
                      ) || null,

                    amount:
                      item?.amount ??
                      null,

                    remark:
                      item?.remark ||
                      "",

                    /**
                     * 关键：
                     * 保留 groupNo
                     */
                    groupNo: groupNo,
                    /**
                     * 如果接口需要，
                     * 同时保留 userId
                     */
                    userId: item.userId || currentUserId,
                    /**
                     * 如果接口需要 updateTime
                     */
                    updateTime:
                      item?.updateTime?.format?.(
                        "YYYY-MM-DD"
                      ) || null,
                  })
                );
              }
            )
            .filter(
              (item) =>
                item?.startDate ||
                item?.endDate ||
                item?.amount !=
                  null ||
                item?.remark
            );

        /**
         * 没有数据
         */
        if (
          !workerSalaryAdjustments.length
        ) {
          message.warning(
            "Please add at least one adjustment record."
          );

          return;
        }

        setSubmitLoading(
          true
        );

        const response =
          await http.post(
            api.getMonthlyWorkerSalary,
            {
              id:
                currentRecord?.id,

              workerSalaryAdjustments,
            }
          );

        if (
          response?.code ===
          "1"
        ) {
          message.success(
            "Change contract amount successfully."
          );

          handleCancel();

          onSuccess?.();

          return;
        }

        commonFn.modalFailedFn(
          response
        );
      } catch (error) {
        /**
         * Form 校验失败
         */
        if (
          error?.errorFields
        ) {
          return;
        }

        message.error(
          "Submit failed."
        );
      } finally {
        setSubmitLoading(
          false
        );
      }
    };

    /**
     * 渲染一个 Contract
     */
    const renderContract = (
      contractField,
      contractIndex,
      removeContract
    ) => {
      return (
        <div
          key={
            contractField.key
          }
          style={{
            border:
              "1px solid #e5e7eb",

            borderRadius: 12,

            padding:
              "12px 12px 8px",

            marginBottom: 12,

            background:
              "#fff",
          }}
        >
          {/* Contract 标题 */}
          <div
            style={{
              fontSize: 14,

              fontWeight: 500,

              marginBottom: 10,

              color: "#202020",
            }}
          >
            {`CONTRACT ${
              contractIndex + 1
            }`}
          </div>

          <Form.List
            name={[
              contractField.name,
              "rows",
            ]}
          >
            {(
              fields,
              {
                add,
                remove,
              }
            ) => (
              <>
                {/* 表头 */}
                <Row
                  gutter={8}
                  style={{
                    marginBottom: 8,
                    fontWeight: 500,
                  }}
                >
                  <Col span={4}>
                    Start Date
                  </Col>

                  <Col span={4}>
                    Amount
                  </Col>

                  <Col span={4}>
                    End Date
                  </Col>

                  <Col span={5}>
                    Remark
                  </Col>

                  <Col span={3}>
                    User Id
                  </Col>

                  <Col span={4}>
                    Update Date
                  </Col>
                </Row>

                {/* Rows */}
                {fields.map(
                  (field) => (
                    <Row
                      key={
                        field.key
                      }
                      gutter={8}
                      align="top"
                      style={{
                        marginBottom: '-16px',
                      }}
                    >
                      {/* Start Date */}
                      <Col span={4}>
                        <Form.Item
                          name={[
                            field.name,
                            "startDate",
                          ]}
                        >
                          <DatePicker
                            style={{
                              width:
                                "100%",
                            }}
                            format="YYYY-MM-DD"
                            placeholder="Select"
                          />
                        </Form.Item>
                      </Col>

                      {/* Amount */}
                      <Col span={4}>
                        {/* <Form.Item
                          name={[
                            field.name,
                            "amount",
                          ]}
                        >
                          <InputNumber
                            style={{
                              width:
                                "100%",
                            }}
                            controls={
                              false
                            }
                            prefix="$"
                            placeholder="Enter Amount"
                            formatter={(
                              value
                            ) => {
                              if (
                                value ===
                                  undefined ||
                                value ===
                                  null
                              ) {
                                return "";
                              }

                              return commonFn.inputAmountFormat(
                                value,
                                false,
                                false
                              );
                            }}
                            parser={(
                              value
                            ) =>
                              commonFn.inputAmountFormat(
                                value,
                                true,
                                false
                              )
                            }
                          />
                        </Form.Item> */}

                        {commonFn.formInputNumberDoc({
                          form,
                          name: [field.name, "amount"],
                          label: '',
                          isNoRequired: true,
                          allowNegative: true,
                        })}

                        {/* {commonFn.interestRateDoc({
                          form,
                          name: "amount",
                          label: field.name,
                          allowNegative: true,
                          isNoRequired: true,
                          validator: (_, value, callback) => {
                            console.log(value, '===value');
                            // if(!value && value != 0)return
                            if(((value || value === 0) && value <= 0 || value > 100)){
                              callback('The Ratio Must Be Between 0 And 100.')
                            } else {
                              callback()
                            }
                          }
                        })} */}
                      </Col>

                      {/* End Date */}
                      <Col span={4}>
                        <Form.Item
                          name={[
                            field.name,
                            "endDate",
                          ]}
                        >
                          <DatePicker
                            style={{
                              width:
                                "100%",
                            }}
                            format="YYYY-MM-DD"
                            placeholder="Select"
                          />
                        </Form.Item>
                      </Col>

                      {/* Remark */}
                      <Col span={5}>
                        <Form.Item
                          name={[
                            field.name,
                            "remark",
                          ]}
                        >
                          <Input
                            placeholder="Please input remark"
                          />
                        </Form.Item>
                      </Col>

                      {/* User Id */}
                      <Col span={3}>
                        <Form.Item
                          name={[
                            field.name,
                            "userId",
                          ]}
                        >
                          <Input
                            placeholder="User Id"
                            readOnly
                            disabled
                          />
                        </Form.Item>
                      </Col>

                      {/* Update Date */}
                      <Col span={4}>
                        <Space.Compact
                          style={{
                            width:
                              "100%",
                          }}
                        >
                          <Form.Item
                            name={[
                              field.name,
                              "updateTime",
                            ]}
                            style={{
                              flex: 1,
                              marginBottom: 0,
                            }}
                          >
                            <DatePicker
                              disabled
                              style={{
                                width:
                                  "100%",
                              }}
                              format="YYYY-MM-DD"
                            />
                          </Form.Item>

                          {/* 删除 Row */}
                          {/* {fields.length >
                            1 && (
                            <Button
                              type="text"
                              danger
                              onClick={() =>
                                remove(
                                  field.name
                                )
                              }
                              icon={
                                <img
                                  src={
                                    deleteIcon
                                  }
                                  alt="delete"
                                  width={
                                    16
                                  }
                                />
                              }
                            />
                          )} */}
                        </Space.Compact>
                      </Col>
                    </Row>
                  )
                )}

                {/* Add Row */}
                <Button
                  type="dashed"
                  block
                  style={{
                    marginTop: 4,
                    marginBottom: 4,
                  }}
                  icon={
                    <PlusOutlined />
                  }
                  onClick={() =>
                    add(
                      getDefaultRow(
                        contractField
                          .name !=
                          null
                          ? contractField
                              .name +
                              1
                          : contractIndex +
                            1,
                        currentUserId
                      )
                    )
                  }
                >
                  Add Row
                </Button>
              </>
            )}
          </Form.List>

          {/* 删除 Contract */}
          {/* {contractIndex >
            0 && (
            <div
              style={{
                textAlign:
                  "right",
                marginTop: 4,
              }}
            >
              <Button
                type="link"
                danger
                onClick={() =>
                  removeContract(
                    contractField.name
                  )
                }
              >
                Delete Contract
              </Button>
            </div>
          )} */}
        </div>
      );
    };

    return (
      <Modal
        title={
          <>
            <Row
              justify="start"
              align="middle"
              className="model_title"
            >
              <Col>
                <EditOutlined className="model_title_mr10" />
              </Col>

              <Col>
                <Typography.Title
                  level={4}
                  className="mg"
                >
                  CHANGE CONTRACT AMOUNT
                </Typography.Title>
              </Col>
            </Row>

            <div
              style={{
                borderTop:
                  "3px solid #3c8dbc",
                marginTop: "8px",
                marginBottom:
                  "19px",
              }}
            />
          </>
        }
        centered
        width="60%"
        open={visible}
        confirmLoading={
          openLoading
        }
        onCancel={
          handleCancel
        }
        maskClosable={false}
        styles={{
          body: {
            minHeight: 260,
          },
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent:
                "center",
              gap: "16px",
            }}
          >
            <Button
              onClick={
                handleCancel
              }
              disabled={
                openLoading ||
                submitLoading
              }
            >
              Cancel
            </Button>

            <Button
              type="primary"
              loading={
                submitLoading
              }
              disabled={
                openLoading
              }
              onClick={
                submitFn
              }
            >
              Submit
            </Button>
          </div>
        }
      >
        <Spin
          spinning={
            openLoading
          }
          tip="Loading..."
        >
          <Form
            form={form}
            name="changeAmountForm"
            layout="vertical"
            style={{
              width: "100%",
            }}
          >
            {/* New Contract */}
            <Button
              type="primary"
              className="btnNew"
              icon={
                <PlusOutlined />
              }
              style={{
                marginBottom: 12,
              }}
              onClick={() => {
                const contracts =
                  form.getFieldValue(
                    "contracts"
                  ) || [];

                /**
                 * 找当前最大的 groupNo
                 */
                const maxGroupNo =
                  contracts.reduce(
                    (
                      max,
                      item
                    ) =>
                      Math.max(
                        max,
                        Number(
                          item?.groupNo ||
                            0
                        )
                      ),
                    0
                  );

                const newGroupNo =
                  maxGroupNo + 1;

                form.setFieldsValue({
                  contracts: [
                    ...contracts,
                    getDefaultContract(
                      newGroupNo,
                      currentUserId
                    ),
                  ],
                });
              }}
            >
              New Contract
            </Button>

            {/* Contract */}
            <div style={{maxHeight: '60vh', overflow: 'auto'}}>
              <Form.List name="contracts">
                {(
                  contractFields,
                  {
                    remove:
                      removeContract,
                  }
                ) =>
                  contractFields.map(
                    (
                      contractField,
                      contractIndex
                    ) =>
                      renderContract(
                        contractField,
                        contractIndex,
                        removeContract
                      )
                  )
                }
              </Form.List>
            </div>
          </Form>
        </Spin>
      </Modal>
    );
  }
);

export default ChangeAmountModal;